import { ApolloProvider } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import client from 'api/ApolloClient';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useEffect } from 'react';
import { QueryClientProvider } from 'react-query';
import { Provider, useSelector } from 'react-redux';
import { Router } from 'react-router';
import 'ui/theme/designTokens/cssTokens/index.css';

import { selectUserHasIterableEmail } from 'core/userContext';
import { selectFeatureFlags, selectUserContextForRum } from 'core/userContext/selectors';
import { selectEnabledPreviews } from 'modules/app/selectors';
import { CompatRouter } from 'react-router-dom-v5-compat';
import reducers from 'reducers';
import { registerValidSW } from 'registerServiceWorker';
import { generateRoutes } from 'routes';
import 'semantic-ui-css/semantic.min.css';
import { addUserContextToRUM, initializeRUM } from 'services/observability/datadog';
import { createHistory, initializeStore } from 'store';
import { GlobalStyles, ThemeProvider } from 'ui/theme';
import { queryClient } from 'api/QueryClient';

import { AngularContextProvider } from 'utils/contexts/angular';
import env from 'utils/env';
import { useInitializeCurrentProject } from '../hooks';

import { AppInitializationLoadingScreen } from './AppInitializationLoadingScreen';

const store = initializeStore(reducers);
const history = createHistory(store);

const App = () => {
  // Subscribe to Redux state
  const enabledPreviews = useSelector(selectEnabledPreviews);
  const featureFlags = useSelector(selectFeatureFlags);
  const hasIterableEmail = useSelector(selectUserHasIterableEmail);
  const userContext = useSelector(selectUserContextForRum);

  // Initialize the current project, the service worker, and metrics
  const [isCurrentProjectInitialized, initializeCurrentProject] = useInitializeCurrentProject();
  useEffect(() => {
    initializeCurrentProject();
    registerValidSW();

    if (env.isProdEnv) {
      initializeRUM();
    }
  }, [initializeCurrentProject]);

  useEffect(() => {
    if (env.isProdEnv && !isEmpty(featureFlags)) {
      Object.entries(featureFlags).forEach(([name, value]) => {
        datadogRum.addFeatureFlagEvaluation(name, value);
      });
    }
  }, [featureFlags]);

  useEffect(() => {
    if (env.isProdEnv && userContext.orgId) {
      addUserContextToRUM(userContext);
    }
  }, [userContext]);

  // While the current project is initializing, show a full-page loading screen
  if (!isCurrentProjectInitialized) {
    return <AppInitializationLoadingScreen />;
  }

  // Render the app once initialized
  return (
    <>
      <Router history={history}>
        <CompatRouter>
          {generateRoutes({
            enabledPreviews,
            featureFlags,
            hasIterableEmail,
          })}
        </CompatRouter>
      </Router>
    </>
  );
};

const AppWrapper = () => {
  const reduxStore = useMemo(() => store, []);
  return (
    <AngularContextProvider>
      <ApolloProvider client={client}>
        <Provider store={reduxStore}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <GlobalStyles />
              <App />
            </ThemeProvider>
          </QueryClientProvider>
        </Provider>
      </ApolloProvider>
    </AngularContextProvider>
  );
};

export type AppDispatch = typeof store.dispatch;
export { App };
export default AppWrapper;
